/**
 * A form of settings in VS Code's style: a search field, a table of contents
 * of the groups, and one row per setting with its title, description and
 * control. It draws a `FormModel`, which the settings tab builds from the
 * settings registry and the sdkconfig editor builds from Kconfig.
 */

export type FormValue = boolean | number | string;

export type FormControl =
    | { type: "boolean" }
    | { type: "string" }
    | { type: "enum"; options: readonly { value: string; label: string; description?: string }[] }
    /** Kconfig's `hex` is a number in base 16. */
    | { type: "number"; min?: number; max?: number; integer?: boolean; base?: 10 | 16 };

export interface FormGroup {
    kind: "group";
    id: string;
    title: string;
    description?: string;
    children: FormNode[];
}

export interface FormItem {
    kind: "item";
    id: string;
    title: string;
    description: string;
    control: FormControl;
    /** More text that search matches, such as a Kconfig symbol's `CONFIG_` name. */
    keywords?: string;
    /** Settings under this one, as under a Kconfig `menuconfig` symbol. */
    children?: FormNode[];
}

export type FormNode = FormGroup | FormItem;

export interface ItemState {
    value: FormValue;
    /** Hidden items are not drawn, as Kconfig hides a symbol whose dependencies are off. */
    visible: boolean;
    /** Why the value cannot be changed, when it cannot. */
    readOnly?: string;
    /** The value differs from the default. */
    modified: boolean;
    /**
     * For a choice, the values that can be chosen now; the others are not
     * offered, as a Kconfig choice hides members whose dependencies are off.
     * All of them when unset.
     */
    options?: readonly string[];
}

export interface FormModel {
    /** The groups and items. The form asks again when `onChange` reports `undefined`. */
    tree(): FormNode[];
    state(id: string): ItemState;
    /** Throws with a message for the user when the value is refused. */
    set(id: string, value: FormValue): void;
    reset(id: string): void;
    /** Report which items changed, or `undefined` when the tree itself did. Returns the unsubscribe. */
    onChange(listener: (ids: readonly string[] | undefined) => void): () => void;
}

/** Items modified from their default, as VS Code's `@modified`. */
const MODIFIED_FILTER = "@modified";

/**
 * The part of `nodes` that matches a search: items whose title, description,
 * id or keywords hold every word of the query, and the groups on the way to
 * them. A group whose title matches keeps all of its children.
 */
export function filterTree(nodes: readonly FormNode[], query: string, state?: (id: string) => ItemState): FormNode[] {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    const modified = words.includes(MODIFIED_FILTER);
    const terms = words.filter((word) => word !== MODIFIED_FILTER);
    if (terms.length === 0 && !modified) return [...nodes];
    const matches = (node: FormNode, path: string): boolean => {
        if (modified && (node.kind !== "item" || state?.(node.id).modified !== true)) return false;
        const text = [
            path,
            node.title,
            node.description ?? "",
            node.id,
            node.kind === "item" ? (node.keywords ?? "") : "",
        ]
            .join("\n")
            .toLowerCase();
        return terms.every((term) => text.includes(term));
    };
    const walk = (list: readonly FormNode[], path: string): FormNode[] => {
        const out: FormNode[] = [];
        for (const node of list) {
            const inner = `${path}\n${node.title}`;
            const children = walk(node.children ?? [], inner);
            if (node.kind === "group") {
                if (!modified && terms.length > 0 && terms.every((term) => node.title.toLowerCase().includes(term)))
                    out.push(node);
                else if (children.length > 0) out.push({ ...node, children });
            } else if (matches(node, path) || children.length > 0) {
                out.push({ ...node, children });
            }
        }
        return out;
    };
    return walk(nodes, "");
}

export interface SettingsForm {
    element: HTMLElement;
    focusSearch(): void;
    /** Show the rows matching `query`, as typing it in the search field does. */
    search(query: string): void;
    dispose(): void;
}

export interface FormOptions {
    searchPlaceholder: string;
    /**
     * "compact" draws each setting on one line, title on the left and control
     * on the right, with the description behind an info button: for long
     * lists such as Kconfig's. "full", the default, shows every description.
     */
    layout?: "full" | "compact";
}

/** Rows are drawn per group as they come near the viewport; this is a row's height until then. */
const ROW_ESTIMATE = 64;

export function createSettingsForm(model: FormModel, options: FormOptions): SettingsForm {
    const element = document.createElement("div");
    element.className = "wb-form";
    const compact = options.layout === "compact";
    element.classList.toggle("wb-form-compact", compact);

    const header = document.createElement("div");
    header.className = "wb-form-header";
    const search = document.createElement("input");
    search.type = "text";
    search.className = "wb-form-search";
    search.placeholder = options.searchPlaceholder;
    search.setAttribute("aria-label", options.searchPlaceholder);
    search.spellcheck = false;
    search.autofocus = true;
    const count = document.createElement("span");
    count.className = "wb-form-count";
    header.append(search, count);

    const body = document.createElement("div");
    body.className = "wb-form-body";
    const toc = document.createElement("nav");
    toc.className = "wb-form-toc";
    const content = document.createElement("div");
    content.className = "wb-form-content";
    body.append(toc, content);
    element.append(header, body);

    /** The rows drawn so far, by item id. */
    const rows = new Map<string, Row>();
    const observer = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue;
                observer.unobserve(entry.target);
                pending.get(entry.target)?.();
            }
        },
        { root: content, rootMargin: "600px 0px" },
    );
    /** Fill in a group's rows, for the placeholders the observer watches. */
    const pending = new Map<Element, () => void>();

    const draw = (): void => {
        observer.disconnect();
        pending.clear();
        rows.clear();
        toc.replaceChildren();
        content.replaceChildren();
        const nodes = filterTree(model.tree(), search.value, (id) => model.state(id));
        const searching = search.value.trim() !== "";
        let items = 0;
        const section = (
            node: FormGroup | undefined,
            list: readonly FormNode[],
            depth: number,
            path: string[],
        ): void => {
            const holder = document.createElement("div");
            holder.className = "wb-form-rows";
            const direct = list.filter((child): child is FormItem => child.kind === "item");
            items += direct.length;
            if (node !== undefined) {
                const heading = document.createElement("div");
                heading.className = `wb-form-heading wb-form-depth-${Math.min(depth, 3)}`;
                heading.id = `wb-form-${node.id}`;
                heading.textContent = node.title;
                content.append(heading);
                if (node.description) {
                    const text = document.createElement("div");
                    text.className = "wb-form-group-description";
                    text.textContent = node.description;
                    content.append(text);
                }
                if (depth <= 2) {
                    const link = document.createElement("button");
                    link.type = "button";
                    link.className = `wb-form-toc-item wb-form-depth-${depth}`;
                    link.textContent = node.title;
                    link.addEventListener("click", () => heading.scrollIntoView({ block: "start" }));
                    toc.append(link);
                }
            }
            if (direct.length > 0) {
                content.append(holder);
                const fill = () => {
                    holder.style.minHeight = "";
                    for (const item of direct) drawItem(holder, item, path, searching);
                };
                if (searching || direct.length < 8) fill();
                else {
                    holder.style.minHeight = `${direct.length * ROW_ESTIMATE}px`;
                    pending.set(holder, fill);
                    observer.observe(holder);
                }
            }
            for (const child of list) {
                if (child.kind === "group") section(child, child.children, depth + 1, [...path, child.title]);
            }
        };
        section(undefined, nodes, 0, []);
        count.textContent = searching ? (items === 1 ? "1 setting found" : `${items} settings found`) : "";
        if (nodes.length === 0) {
            const empty = document.createElement("div");
            empty.className = "wb-form-empty";
            empty.textContent = "No settings found";
            content.append(empty);
        }
    };

    const drawItem = (holder: HTMLElement, item: FormItem, path: string[], searching: boolean): void => {
        const row = new Row(model, item, searching ? path : [], compact);
        rows.set(item.id, row);
        holder.append(row.element);
        const children = item.children ?? [];
        if (children.length === 0) return;
        const nested = document.createElement("div");
        nested.className = "wb-form-nested";
        row.nested = nested;
        holder.append(nested);
        for (const child of children) {
            if (child.kind === "item") drawItem(nested, child, path, searching);
        }
        row.update();
    };

    let timer: ReturnType<typeof setTimeout> | undefined;
    search.addEventListener("input", () => {
        clearTimeout(timer);
        timer = setTimeout(draw, 150);
    });
    search.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && search.value !== "") {
            search.value = "";
            draw();
            event.stopPropagation();
        }
    });

    const unsubscribe = model.onChange((ids) => {
        if (ids === undefined) {
            draw();
            return;
        }
        for (const id of ids) rows.get(id)?.update();
    });
    draw();

    return {
        element,
        focusSearch: () => search.focus(),
        search: (query) => {
            search.value = query;
            draw();
        },
        dispose: () => {
            unsubscribe();
            observer.disconnect();
            clearTimeout(timer);
        },
    };
}

/** One setting's row: modified bar, title, description, control, error and reset. */
class Row {
    readonly element = document.createElement("div");
    /** The rows of the item's own children, shown while the item is. */
    nested: HTMLElement | undefined;
    readonly #model: FormModel;
    readonly #item: FormItem;
    readonly #control: HTMLInputElement | HTMLSelectElement;
    readonly #note = document.createElement("div");
    readonly #error = document.createElement("div");
    readonly #reset = document.createElement("button");

    constructor(model: FormModel, item: FormItem, path: string[], compact: boolean) {
        this.#model = model;
        this.#item = item;
        this.element.className = "wb-form-row";
        this.element.dataset["id"] = item.id;

        const title = document.createElement("div");
        title.className = "wb-form-title";
        if (path.length > 0) {
            const prefix = document.createElement("span");
            prefix.className = "wb-form-path";
            prefix.textContent = `${path.join(" › ")}: `;
            title.append(prefix);
        }
        const id = `wb-form-control-${item.id}`;
        const name = document.createElement(compact ? "label" : "span");
        name.className = "wb-form-name";
        name.textContent = item.title;
        if (name instanceof HTMLLabelElement) name.htmlFor = id;
        title.append(name);
        if (compact && item.description !== "") title.append(this.#infoButton(item));

        this.#reset.type = "button";
        this.#reset.className = "wb-form-reset codicon codicon-discard";
        this.#reset.title = "Reset to the default";
        this.#reset.setAttribute("aria-label", `Reset ${item.title} to the default`);
        this.#reset.addEventListener("click", () => this.#model.reset(item.id));
        title.append(this.#reset);

        const description = document.createElement("div");
        description.className = "wb-form-description";
        description.textContent = item.description;
        this.#note.className = "wb-form-note";
        this.#error.className = "wb-form-error";
        this.#error.setAttribute("role", "alert");
        this.#error.hidden = true;

        this.#control = this.#createControl(id);
        if (compact) {
            this.element.append(title, this.#control, description, this.#note, this.#error);
        } else if (item.control.type === "boolean") {
            const label = document.createElement("label");
            label.className = "wb-form-check";
            label.append(this.#control, description);
            this.element.append(title, label, this.#note, this.#error);
        } else {
            description.id = `${id}-description`;
            this.#control.setAttribute("aria-describedby", description.id);
            this.element.append(title, description, this.#control, this.#note, this.#error);
        }
        this.#control.setAttribute("aria-label", item.title);
        this.update();
    }

    /** The button that shows and hides the description in the compact layout. */
    #infoButton(item: FormItem): HTMLButtonElement {
        const info = document.createElement("button");
        info.type = "button";
        info.className = "wb-form-info codicon codicon-info";
        info.setAttribute("aria-label", `Description of ${item.title}`);
        const show = (open: boolean) => {
            this.element.classList.toggle("wb-form-open", open);
            info.setAttribute("aria-expanded", String(open));
            info.title = open ? "Hide the description" : "Show the description";
        };
        show(false);
        info.addEventListener("click", () => show(!this.element.classList.contains("wb-form-open")));
        return info;
    }

    update(): void {
        const state = this.#model.state(this.#item.id);
        this.element.hidden = !state.visible;
        if (this.nested !== undefined) this.nested.hidden = !state.visible || state.value === false;
        this.element.classList.toggle("wb-form-modified", state.modified);
        this.#reset.hidden = !state.modified || state.readOnly !== undefined;
        this.#control.disabled = state.readOnly !== undefined;
        this.#note.textContent = state.readOnly ?? "";
        this.#note.hidden = state.readOnly === undefined;
        const control = this.#control;
        if (control instanceof HTMLSelectElement) {
            for (const option of Array.from(control.options))
                option.hidden = state.options?.includes(option.value) === false;
        }
        if (control instanceof HTMLInputElement && control.type === "checkbox") control.checked = state.value === true;
        // Leave a field the user is typing in alone until they commit it.
        else if (document.activeElement !== control || control instanceof HTMLSelectElement)
            control.value = this.#format(state.value);
    }

    #createControl(id: string): HTMLInputElement | HTMLSelectElement {
        const spec = this.#item.control;
        if (spec.type === "enum") {
            const select = document.createElement("select");
            select.id = id;
            select.className = "wb-form-select";
            for (const option of spec.options) {
                const element = document.createElement("option");
                element.value = option.value;
                element.textContent = option.label;
                if (option.description) element.title = option.description;
                select.append(element);
            }
            select.addEventListener("change", () => this.#commit(select.value));
            return select;
        }
        const input = document.createElement("input");
        input.id = id;
        if (spec.type === "boolean") {
            input.type = "checkbox";
            input.addEventListener("change", () => this.#commit(input.checked));
            return input;
        }
        input.type = "text";
        input.className = "wb-form-text";
        input.spellcheck = false;
        if (spec.type === "number") input.inputMode = spec.base === 16 ? "text" : "numeric";
        input.addEventListener("change", () => {
            if (spec.type !== "number") this.#commit(input.value);
            else {
                const value = parseNumber(input.value, spec.base ?? 10);
                if (value === undefined)
                    this.#fail(spec.base === 16 ? "Enter a hexadecimal number." : "Enter a number.");
                else this.#commit(value);
            }
        });
        input.addEventListener("keydown", (event) => {
            if (event.key === "Enter") input.dispatchEvent(new Event("change"));
        });
        return input;
    }

    #commit(value: FormValue): void {
        try {
            this.#model.set(this.#item.id, value);
            this.#fail(undefined);
        } catch (e) {
            this.#fail(e instanceof Error ? e.message : String(e));
        }
        this.update();
    }

    #fail(message: string | undefined): void {
        this.#error.textContent = message ?? "";
        this.#error.hidden = message === undefined;
        this.#control.toggleAttribute("aria-invalid", message !== undefined);
    }

    #format(value: FormValue): string {
        const spec = this.#item.control;
        if (spec.type === "number" && spec.base === 16 && typeof value === "number") return `0x${value.toString(16)}`;
        return String(value);
    }
}

export function parseNumber(text: string, base: 10 | 16): number | undefined {
    const trimmed = text.trim();
    const valid = base === 16 ? /^(0x)?[0-9a-f]+$/i : /^-?\d+(\.\d+)?$/;
    if (!valid.test(trimmed)) return undefined;
    return base === 16 ? parseInt(trimmed.replace(/^0x/i, ""), 16) : Number(trimmed);
}
